import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Bot, Send, X } from 'lucide-react';
import { db } from '../firebase';
import BBALoader from './BBALoader';
import MeetingSummaryContent from './MeetingSummaryContent';
import MeetingSummaryActionItems from './MeetingSummaryActionItems';
import { parseFathomSummarySections } from '../utils/fathomSummaryParser';
import { askMeetingSummaryQuestion } from '../services/openaiService';

const SUMMARY_TABS = [
  { id: 'keyTakeaways', label: 'Key Takeaways' },
  { id: 'topics', label: 'Topics' },
  { id: 'nextSteps', label: 'Next Steps' },
  { id: 'actionItems', label: 'Action Items' },
];

/**
 * Shared Fathom meeting summary modal (Client + Lead overviews).
 * Parses the Fathom markdown into Purpose + tabbed sections, lists action
 * items alongside their synced tasks, and offers an AI Q&A panel.
 */
export default function MeetingSummaryModal({ open, event, summary, loading, onClose }) {
  const [summaryTab, setSummaryTab] = useState('keyTakeaways');
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');
  const [showTranscription, setShowTranscription] = useState(false);
  const [fathomTasks, setFathomTasks] = useState([]);

  // Fresh view state for every summary opened
  useEffect(() => {
    setSummaryTab('keyTakeaways');
    setChatOpen(false);
    setChatMessages([]);
    setChatInput('');
    setChatLoading(false);
    setChatError('');
    setShowTranscription(false);
    setFathomTasks([]);
  }, [open, summary]);

  const recordingId = summary?.recordingId;
  useEffect(() => {
    if (!open || !recordingId) return undefined;
    let cancelled = false;
    getDocs(query(collection(db, 'tasks'), where('fathomRecordingId', '==', recordingId)))
      .then((snap) => {
        if (!cancelled) setFathomTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((err) => {
        console.warn('Could not load Fathom tasks for meeting summary:', err);
        if (!cancelled) setFathomTasks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, recordingId]);

  const parsed = useMemo(() => {
    if (!summary?.summary) {
      return { meetingPurpose: '', keyTakeaways: '', topics: '', nextSteps: '', hasStructuredSections: false };
    }
    return parseFathomSummarySections(summary.summary);
  }, [summary?.summary]);

  const activeTabContent = useMemo(() => {
    if (summaryTab === 'topics') return parsed.topics;
    if (summaryTab === 'nextSteps') return parsed.nextSteps || '';
    if (summaryTab === 'actionItems') return null;
    return parsed.keyTakeaways;
  }, [summaryTab, parsed]);

  const handleSendChat = async () => {
    const question = chatInput.trim();
    if (!question || chatLoading) return;

    setChatLoading(true);
    setChatError('');
    setChatInput('');

    const nextMessages = [...chatMessages, { role: 'user', content: question }];
    setChatMessages(nextMessages);

    try {
      const answer = await askMeetingSummaryQuestion({
        summaryText: summary?.summary || '',
        transcription: summary?.transcription || '',
        question,
        messages: chatMessages,
      });
      setChatMessages([...nextMessages, { role: 'assistant', content: answer }]);
    } catch (error) {
      setChatError(error.message || 'Failed to get an answer. Please try again.');
      setChatMessages(chatMessages);
      setChatInput(question);
    } finally {
      setChatLoading(false);
    }
  };

  if (!open) return null;

  const eventStart = event ? new Date(event.start || event.startTime) : null;
  const actionItems = Array.isArray(summary?.actionItems) ? summary.actionItems : [];

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4 pb-4 border-b border-gray-200 dark:border-gray-600">
          <div className="flex-1 min-w-0 pr-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Meeting Summary
            </h3>
            {event && (
              <>
                <p className="text-lg font-medium text-gray-900 dark:text-white mt-1">
                  {event.title || event.summary || 'Untitled Event'}
                </p>
                {eventStart && !Number.isNaN(eventStart.getTime()) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {eventStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    {' · '}
                    {eventStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </p>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => {
                setChatOpen((o) => !o);
                setChatError('');
              }}
              className={`p-1.5 rounded-lg transition-colors ${
                chatOpen
                  ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title="Ask AI about this meeting"
            >
              <Bot className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <BBALoader size={48} />
          </div>
        ) : summary ? (
          <div className="space-y-4">
            {parsed.meetingPurpose && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Meeting Purpose</h4>
                <MeetingSummaryContent markdown={parsed.meetingPurpose} />
              </div>
            )}

            <div className="border-b border-gray-200 dark:border-gray-600">
              <nav className="flex space-x-1">
                {SUMMARY_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSummaryTab(tab.id)}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                      summaryTab === tab.id
                        ? 'bg-red-600 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {summaryTab === 'actionItems' ? (
              <MeetingSummaryActionItems actionItems={actionItems} tasks={fathomTasks} />
            ) : activeTabContent ? (
              <MeetingSummaryContent markdown={activeTabContent} />
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                No content available for this section.
              </div>
            )}

            {chatOpen && (
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600 flex items-center gap-2">
                  <Bot className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Ask about this meeting</span>
                </div>
                <div className="h-48 overflow-y-auto p-4 space-y-3 bg-white dark:bg-gray-800">
                  {chatMessages.length === 0 && !chatLoading && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Ask a question about key decisions, action items, or topics from this meeting.
                    </p>
                  )}
                  {chatMessages.map((msg, index) => (
                    <div
                      key={index}
                      className={`text-sm ${
                        msg.role === 'user'
                          ? 'text-gray-900 dark:text-white'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <span className="font-semibold">
                        {msg.role === 'user' ? 'You: ' : 'AI: '}
                      </span>
                      {msg.content}
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <BBALoader size={16} />
                      Thinking...
                    </div>
                  )}
                </div>
                {chatError && (
                  <div className="px-4 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-t border-red-100 dark:border-red-900/40">
                    {chatError}
                  </div>
                )}
                <div className="flex gap-2 p-3 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendChat();
                      }
                    }}
                    placeholder="Ask a question..."
                    disabled={chatLoading}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={handleSendChat}
                    disabled={chatLoading || !chatInput.trim()}
                    className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {summary.transcription && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowTranscription((o) => !o)}
                  className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 hover:text-red-600 dark:hover:text-red-400"
                >
                  {showTranscription ? 'Hide Transcription' : 'Show Transcription'}
                </button>
                {showTranscription && (
                  <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4 text-gray-900 dark:text-white whitespace-pre-wrap text-sm max-h-96 overflow-y-auto">
                    {summary.transcription}
                  </div>
                )}
              </div>
            )}

            {summary.createdAt && (
              <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-600">
                Generated: {summary.createdAt.toDate
                  ? summary.createdAt.toDate().toLocaleString('en-US')
                  : new Date(summary.createdAt).toLocaleString('en-US')}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <p className="mb-2">No meeting summary available for this event.</p>
            <p className="text-sm">The AI transcription summary may still be processing or was not generated for this meeting.</p>
          </div>
        )}
      </div>
    </div>
  );
}
